import React from 'react';
import { View } from 'react-native';
import { useQuery } from '@tanstack/react-query';
import { fmtDate, fmtTime, inr, minutesToHm, P, periodLabel } from '@hrms/shared';
import { useAuth } from '../../../auth/AuthProvider';
import { api } from '../../../lib/api';
import { fmtWeekday, greeting } from '../../../lib/date';
import { openFile } from '../../../lib/files';
import { useGo } from '../../../navigation/nav';
import { Button, Card, Columns, Dot, EmptyState, ErrorState, Grid, Loading, PageHeader, Row, Screen, Stack, StatCard, StatusBadge, Text, useTheme } from '../../../ui';

function AdminHome() {
  const { me, can } = useAuth();
  const go = useGo();
  return (
    <Screen>
      <PageHeader title={`${greeting()}, ${me?.name?.split(' ')[0] ?? ''}`} subtitle={me?.tenant?.name} />
      <Card>
        <EmptyState
          icon="grid"
          title="Welcome to your admin workspace"
          description="Use the menu or search (Ctrl/⌘ K on web) to jump to any screen."
          action={
            <Row>
              {can(P.EMPLOYEES_VIEW, P.EMPLOYEES_MANAGE) && <Button title="HR dashboard" icon="chart" onPress={() => go('HrDashboard')} />}
              {can(P.PAYROLL_VIEW, P.PAYROLL_MANAGE) && <Button title="Payroll" icon="play" onPress={() => go('PayrollRuns')} />}
              {can(P.TENANT_MANAGE, P.TENANT_VIEW) && <Button title="Settings" icon="settings" onPress={() => go('TenantSettings')} />}
            </Row>
          }
        />
      </Card>
    </Screen>
  );
}

export function HomeScreen() {
  const { me, can } = useAuth();
  const go = useGo();
  const { colors } = useTheme();
  const isEmployee = !!me?.employee && can(P.SELF_SERVICE);
  const d = useQuery({ queryKey: ['dashboard', 'employee'], queryFn: () => api.dashboard.employee(), enabled: isEmployee });
  const isManager = can(P.EMPLOYEES_TEAM, P.LEAVE_TEAM, P.ATTENDANCE_TEAM);
  const m = useQuery({ queryKey: ['dashboard', 'manager'], queryFn: () => api.dashboard.manager(), enabled: isManager });

  if (!isEmployee) return <AdminHome />;
  if (d.isLoading) return <Loading />;
  if (d.isError) return <Screen><ErrorState error={d.error} onRetry={() => d.refetch()} /></Screen>;
  const data = d.data;
  const punchedIn = (data?.punches_today.length ?? 0) % 2 === 1;

  return (
    <Screen testID="home-dashboard" refreshing={d.isRefetching} onRefresh={() => { d.refetch(); m.refetch(); }}>
      <PageHeader
        title={`${greeting()}, ${data?.employee?.full_name.split(' ')[0] ?? me?.name}`}
        subtitle={`${fmtWeekday()}${data?.employee?.designation ? ` · ${data.employee.designation}` : ''}`}
        actions={
          <>
            <Button variant="primary" icon="clock" title={punchedIn ? 'Punch out' : 'Punch in'} onPress={() => go('Punch')} testID="home-punch" />
            <Button icon="leaf" title="Apply leave" onPress={() => go('ApplyLeave')} />
          </>
        }
      />
      <Grid min={150}>
        <StatCard
          label="Today"
          icon="clock"
          tone={data?.today?.status === 'present' ? 'success' : 'info'}
          value={data?.today ? minutesToHm(data.today.worked_minutes) : punchedIn ? 'In' : '—'}
          hint={data?.punches_today.length ? `First in ${fmtTime(data.punches_today[0].punched_at)}` : 'Not punched in yet'}
          onPress={() => go('MyAttendance')}
        />
        <StatCard label="Pending requests" icon="inbox" tone="warning" value={data?.pending_requests ?? 0} onPress={() => go('ApplyLeave')} />
        <StatCard label="Unread notifications" icon="bell" tone="info" value={data?.unread_notifications ?? 0} onPress={() => go('Notifications')} />
        <StatCard label="Last net pay" icon="wallet" tone="success" value={data?.latest_payslip ? inr(data.latest_payslip.net_pay) : '—'} hint={data?.latest_payslip ? periodLabel(data.latest_payslip.period) : undefined} onPress={() => go('Payslips')} />
      </Grid>
      <Columns>
        <Stack>
          <Card title="Leave balances" actions={<Button size="sm" variant="ghost" title="Details" onPress={() => go('LeaveBalance')} />}>
            {!data?.leave_balances.length ? (
              <Text variant="muted">No leave balances yet.</Text>
            ) : (
              <Grid min={140} gap={10}>
                {data.leave_balances.map((b) => (
                  <View key={b.id} style={{ padding: 12, borderWidth: 1, borderColor: colors.border, borderRadius: 10 }}>
                    <Row gap={6}>
                      <Dot color={b.leave_type.color} />
                      <Text variant="muted" numberOfLines={1} style={{ flex: 1 }}>
                        {b.leave_type.name}
                      </Text>
                    </Row>
                    <Text size={22} weight="semibold" style={{ marginTop: 4 }}>
                      {b.balance}
                    </Text>
                    <Text variant="small">
                      used {b.used}
                      {b.pending ? ` · ${b.pending} pending` : ''}
                    </Text>
                  </View>
                ))}
              </Grid>
            )}
          </Card>
          {isManager && m.data && (
            <Card title="My team today" actions={<Button size="sm" variant="ghost" title="Team dashboard" onPress={() => go('TeamDashboard')} />}>
              <Grid min={120} gap={10}>
                <StatCard label="Present" value={m.data.attendance_today.present} icon="check" tone="success" />
                <StatCard label="On leave" value={m.data.attendance_today.on_leave} icon="leaf" tone="info" />
                <StatCard label="Late" value={m.data.attendance_today.late} icon="clock" tone="warning" />
                <StatCard label="Approvals" value={m.data.pending_approvals} icon="inbox" tone="primary" onPress={() => go('Approvals')} />
              </Grid>
            </Card>
          )}
        </Stack>
        <Stack>
          <Card title="Upcoming holidays">
            {!data?.upcoming_holidays.length ? (
              <Text variant="muted">No upcoming holidays.</Text>
            ) : (
              <Stack gap={10}>
                {data.upcoming_holidays.slice(0, 6).map((h) => (
                  <Row key={h.id} justify="space-between" wrap={false}>
                    <Text style={{ flex: 1 }} numberOfLines={1}>
                      {h.name}
                      {h.is_optional ? <Text variant="small"> (optional)</Text> : null}
                    </Text>
                    <Text variant="muted">{fmtDate(h.date)}</Text>
                  </Row>
                ))}
              </Stack>
            )}
          </Card>
          {data?.latest_payslip && (
            <Card title="Latest payslip" subtitle={periodLabel(data.latest_payslip.period)}>
              <Row justify="space-between">
                <View>
                  <Text size={22} weight="semibold">
                    {inr(data.latest_payslip.net_pay)}
                  </Text>
                  <Text variant="small">Gross {inr(data.latest_payslip.gross)}</Text>
                </View>
                <Button icon="eye" title="View" onPress={() => openFile(api.payroll.payslipHtmlPath(data.latest_payslip!.id))} />
              </Row>
            </Card>
          )}
          {data?.today && (
            <Card title="Today's status">
              <StatusBadge status={data.today.status} />
            </Card>
          )}
        </Stack>
      </Columns>
    </Screen>
  );
}
